import {
  DeliveryLedger,
  TIMING,
  decodeServerMessage,
  encodeClientMessage,
  reconnectDelayMs,
  type ClientMessage,
  type ServerMessage,
} from '@konvoi/shared';

/**
 * Die Verbindung zum Backend.
 *
 * Auf Landstraßen reißt sie regelmäßig ab. Deshalb ist Wiederverbinden hier
 * kein Sonderfall, sondern der Normalbetrieb: Die Wartezeit wächst mit jedem
 * Versuch, damit ein länger ausgefallener Server nicht von zehn Fahrzeugen
 * gleichzeitig bestürmt wird, und sie streut zufällig, damit nicht alle im
 * selben Augenblick wiederkommen.
 *
 * Beim erneuten Anmelden nennt der Client die höchste ihm bekannte
 * Spurpunkt-Nummer. Der Server schickt dann nur, was seither dazugekommen ist.
 */

export type ConnectionStatus = 'connecting' | 'open' | 'reconnecting' | 'closed';

export interface ConnectionOptions {
  url: string;
  tourId: string;
  participantId: string;
  sessionToken: string;
  /** Höchste bereits bekannte Spurpunkt-Nummer. */
  knownTrackSeq: () => number | null;
  onMessage: (message: ServerMessage) => void;
  onStatus: (status: ConnectionStatus) => void;
  /** Wird nach jeder erfolgreichen Anmeldung gerufen — Gelegenheit zum Nachsenden. */
  onReady?: () => void;
  /**
   * Was mit `sendTracked` hinausging und von keiner Antwort bestätigt war, als
   * sich die Verbindung als tot herausstellte — in der Reihenfolge des Sendens.
   * Es ist womöglich nie angekommen und gehört zurück in den Puffer (A-137).
   */
  onUndelivered?: (messages: readonly ClientMessage[]) => void;
  /**
   * Die Verbindung wurde aufgegeben — vom Herzschlag, oder weil auf die
   * Anmeldung nichts kam. Nur zum Mitschreiben (A-139): Wann das geschieht,
   * weiß allein das Gerät.
   */
  onGaveUp?: (reason: GiveUpReason) => void;
}

/** Warum eine Verbindung aufgegeben wurde. */
export type GiveUpReason = 'heartbeat' | 'join_unanswered';

export class TourConnection {
  #socket: WebSocket | null = null;
  #attempt = 0;
  #closedByUs = false;
  #heartbeat: ReturnType<typeof setInterval> | null = null;
  #lastPong = 0;
  #reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  /**
   * Läuft zwischen dem Öffnen des Sockets und der ersten Antwort des Servers.
   *
   * Solange sie läuft, gilt die Verbindung **nicht** als offen: Der Socket
   * steht, aber ob die Anmeldung angekommen ist, weiß niemand (A-107).
   */
  #joinTimer: ReturnType<typeof setTimeout> | null = null;
  /** Hat der Server auf dieser Verbindung schon etwas gesagt? */
  #bestaetigt = false;
  /**
   * Was auf dem aktuellen Socket hinausging und was davon bestätigt ist.
   * Gehört zum Socket wie `#bestaetigt` und beginnt mit jedem neuen von vorn.
   */
  #ledger = new DeliveryLedger<ClientMessage>();
  #onlineListener: (() => void) | null = null;

  constructor(private readonly options: ConnectionOptions) {}

  /**
   * Verbunden heißt: Der Socket steht **und** der Server hat geantwortet.
   *
   * Der zweite Teil ist neu und der ganze Punkt. Ohne ihn wanderten Positionen
   * in einen Socket, von dem niemand weiß, ob die Anmeldung je ankam — statt in
   * den Funklochpuffer, der genau dafür da ist.
   */
  get connected(): boolean {
    return this.#socket?.readyState === WebSocket.OPEN && this.#bestaetigt;
  }

  open(): void {
    this.#closedByUs = false;

    /*
     * Kommt das Netz zurück, wird sofort verbunden — nicht erst nach der
     * angesammelten Wartezeit.
     *
     * Die ansteigende Wartezeit ist richtig und steht so im Auftrag,
     * Abschnitt 9: Sie verhindert, dass eine ganze Kolonne einen
     * wiederkehrenden Server niederrennt. Nur muss sie zurückgesetzt werden,
     * sobald der Anlass wegfällt.
     *
     * Am 27.08.2026 auf der Ausfahrt „Haaren-Salzkotten-Atteln" fiel auf, dass
     * ein Gerät nach einem längeren Funkloch deutlich länger brauchte als ein
     * zweites im selben Wagen. Der Grund: Nach einer halben Minute im Loch steht
     * die Wartezeit bei `reconnectMaxMs` — dreissig Sekunden —, und ohne diesen
     * Zuhörer sass das Gerät sie ab, obwohl es längst wieder gekonnt hätte.
     *
     * `online` ist dabei ein Hinweis und keine Zusicherung: Der Browser meldet,
     * dass eine Schnittstelle da ist, nicht dass der Server erreichbar wäre.
     * Schlägt der Versuch fehl, greift die Wartezeit wie zuvor — nur eben von
     * vorn.
     */
    this.#onlineListener = () => {
      this.jetztVersuchen();
    };
    window.addEventListener('online', this.#onlineListener);

    this.connect();
  }

  /**
   * Sofort einen neuen Versuch wagen und die angesammelte Wartezeit vergessen.
   *
   * Für die Augenblicke, in denen sich die Lage von aussen erkennbar geändert
   * hat: Das Netz ist zurück, oder die App kommt in den Vordergrund. Solange
   * eine Verbindung steht, geschieht nichts.
   */
  jetztVersuchen(): void {
    if (this.#closedByUs || this.connected) {
      return;
    }
    if (this.#socket !== null && this.#socket.readyState === WebSocket.CONNECTING) {
      // Es läuft bereits ein Versuch — ein zweiter brächte nichts.
      return;
    }
    if (this.#reconnectTimer !== null) {
      clearTimeout(this.#reconnectTimer);
      this.#reconnectTimer = null;
    }
    this.#attempt = 0;
    this.connect();
  }

  private connect(): void {
    this.options.onStatus(this.#attempt === 0 ? 'connecting' : 'reconnecting');

    const vorheriger = this.#socket;

    /*
     * Was der Vorgänger nicht nachweislich zugestellt hat, wird hier
     * abgeschrieben — sein `close` bekommt gleich keinen Zugriff mehr.
     *
     * Meist ist dann nichts mehr offen: Herzschlag und `close` haben es schon
     * herausgegeben. Aber der Browser kann einen Socket auf `CLOSED` setzen
     * und das `close` erst danach zustellen. Kommt dazwischen `online` oder die
     * App in den Vordergrund, ersetzt `jetztVersuchen` den Socket, und das
     * verspätete `close` fiele am Riegel unten ab — samt allem, was noch offen
     * war.
     */
    this.writeOff();

    const socket = new WebSocket(this.options.url);
    this.#socket = socket;
    this.#ledger = new DeliveryLedger();

    /*
     * Die Bestätigung gehört dem Socket, nicht der Verbindung: Was der Server
     * auf der vorigen gesagt hat, sagt über diese nichts. Zurückgesetzt wurde
     * sie bisher nur im `close`-Hörer — und damit ausgerechnet von dem Socket,
     * der gar nicht mehr zuständig war. Mit dem Riegel unten fiele sie sonst
     * ganz aus, und eine neue Verbindung gälte als tragend, bevor eine Antwort
     * da ist.
     */
    this.#bestaetigt = false;

    /*
     * Der Vorgänger wird abgeräumt, **nachdem** der neue Socket eingetragen
     * ist. Nur in dieser Reihenfolge greift der Riegel unten: Sein `close`
     * findet sich dann nicht mehr als zuständig und lässt den gemeinsamen
     * Zustand in Ruhe.
     */
    vorheriger?.close();

    socket.addEventListener('open', () => {
      if (this.#socket !== socket) {
        return;
      }
      /*
       * Der Versuchszähler wird **nicht** hier zurückgesetzt, sondern erst mit
       * der ersten Antwort. Sonst liefe ein Socket, der aufgeht und ohne
       * Anmeldung wieder zufällt, ohne jede Wartezeit im Kreis — und träfe den
       * Server am Rand eines Funklochs im Sekundentakt.
       */
      this.#lastPong = Date.now();

      const sinceTrackSeq = this.options.knownTrackSeq();
      this.sendRaw({
        type: 'join',
        tourId: this.options.tourId,
        participantId: this.options.participantId,
        sessionToken: this.options.sessionToken,
        ...(sinceTrackSeq === null ? {} : { sinceTrackSeq }),
      });

      /*
       * Noch **nicht** „open". Die Anmeldung ist unterwegs, mehr nicht — und
       * am Rand eines Funklochs kommt der Handschlag durch, während der erste
       * Rahmen danach verlorengeht. Bis zur ersten Antwort bleibt der Zustand
       * deshalb „verbindet" und der Puffer nimmt die Messungen auf.
       */
      this.startHeartbeat();
      this.#joinTimer = setTimeout(() => {
        if (this.#socket === socket && !this.#bestaetigt) {
          // Nichts gehört. Der Socket sieht offen aus und trägt nicht —
          // aufgeben, damit die Wiederverbindung greift.
          this.aufgeben('join_unanswered');
        }
      }, TIMING.joinConfirmTimeoutMs);
    });

    socket.addEventListener('message', (event: MessageEvent<string>) => {
      if (this.#socket !== socket) {
        return;
      }
      const ergebnis = decodeServerMessage(event.data);
      if (!ergebnis.ok) {
        return;
      }
      /*
       * Irgendeine Antwort genügt als Beleg, dass die Verbindung trägt — auch
       * ein `pong`. Auf `tour_state` zu warten wäre enger, aber diese Nachricht
       * ist die größte, die es gibt, und auf einer schmalen Leitung dauert sie.
       */
      this.bestaetige();

      if (ergebnis.message.type === 'pong') {
        this.#lastPong = Date.now();
        this.#ledger.pongReceived(ergebnis.message.sentAt);
        return;
      }
      this.options.onMessage(ergebnis.message);
    });

    socket.addEventListener('close', () => {
      /*
       * Der Riegel, um den es geht (A-111).
       *
       * iOS hält eine Seite im Hintergrund an. Der Socket stirbt dabei, sein
       * `close` wird aber erst beim Zurückholen zugestellt — da steht die neue
       * Verbindung längst. Ohne diese Zeile setzte der tote Socket dann die
       * Bestätigung zurück, hielte den Herzschlag der lebenden Verbindung an
       * und bestellte eine weitere. Die meldet sich an, lässt ihrerseits einen
       * Socket zurück, und die Schleife hält sich selbst am Leben: Auf der
       * Ausfahrt 493228 am 14.09.2026 sechs Doppelanmeldungen und ein Schild
       * „Keine Verbindung", das kurz wegging und wiederkam, während 128
       * Positionen in zweieinhalb Minuten live beim Server ankamen.
       */
      if (this.#socket !== socket) {
        return;
      }
      this.stopHeartbeat();
      this.stopJoinTimer();
      this.#bestaetigt = false;
      if (this.#closedByUs) {
        this.options.onStatus('closed');
        return;
      }
      this.writeOff();
      this.scheduleReconnect();
    });

    socket.addEventListener('error', () => {
      // Der Fehler selbst sagt im Browser nichts Verwertbares; das Schließen
      // folgt ohnehin und übernimmt das Wiederverbinden. Hier braucht es
      // keinen Riegel: Geschlossen wird nur der eigene Socket, und ein
      // abgelegter soll ohnehin zu.
      socket.close();
    });
  }

  private scheduleReconnect(): void {
    this.options.onStatus('reconnecting');

    // Die Rechnung steht in shared — der Simulator wartet nach demselben
    // Muster, und was beide angeht, gehört nicht zweimal aufgeschrieben.
    const wartezeit = reconnectDelayMs(this.#attempt);
    this.#attempt += 1;

    this.#reconnectTimer = setTimeout(() => {
      this.connect();
    }, wartezeit);
  }

  /**
   * Herzschlag.
   *
   * Eine Mobilfunkverbindung, die im Tunnel abreißt, wird von keiner Seite
   * geschlossen — sie schweigt nur. Ohne diesen Takt hielte der Client sich für
   * verbunden und schriebe seine Positionen ins Leere.
   *
   * Mit ihm tut er das nur noch bis zur Erkennung, und das sind bis zu
   * `heartbeatTimeoutMs` plus ein Takt. Was in dieser Zeit ins Leere ging, gibt
   * das Zustellbuch zurück (A-137).
   */
  private startHeartbeat(): void {
    this.stopHeartbeat();
    this.#heartbeat = setInterval(() => {
      if (Date.now() - this.#lastPong > TIMING.heartbeatTimeoutMs) {
        this.aufgeben('heartbeat');
        return;
      }
      this.sendRaw({ type: 'ping', sentAt: Date.now() });
    }, TIMING.heartbeatIntervalMs);
  }

  /**
   * Die Verbindung ist tot — und zwar jetzt, nicht erst, wenn der Browser es
   * bestätigt.
   *
   * Auf einer toten Leitung stellt der Browser das `close` erst zu, wenn er
   * den Abbau aufgegeben hat. Bis zum 18.09.2026 wartete die App darauf, und
   * auf der Fahrt 926216 stand deshalb zweimal „63 Positionen warten" ohne
   * „Keine Verbindung" da — bis zu einer halben Minute lang, und so lange
   * wartete auch der nächste Versuch (A-138). Die App wusste, dass die
   * Verbindung weg war, und sagte es nicht.
   *
   * Deshalb hier alles auf einmal: abschreiben, was nicht bestätigt ist, den
   * Socket loslassen, „wiederverbinden" melden und den nächsten Versuch
   * einplanen. Losgelassen heißt: Er ist nicht mehr `#socket`. Sein
   * verspätetes `close` fällt damit am Riegel aus A-111 ab und bestellt
   * keinen zweiten Versuch.
   *
   * Abgeschrieben wird **vor** dem Schließen — bis zum `close` lägen die
   * verlorenen Messungen sonst nur im Speicher statt im Puffer, der ein
   * Beenden der App übersteht (A-137).
   */
  private aufgeben(grund: GiveUpReason): void {
    const toter = this.#socket;
    this.writeOff();
    this.#socket = null;
    this.stopHeartbeat();
    this.stopJoinTimer();
    this.#bestaetigt = false;
    // Erst jetzt: Wer darauf etwas senden will, soll es puffern und nicht in
    // den Socket schreiben, der gerade aufgegeben wird.
    this.options.onGaveUp?.(grund);
    toter?.close();
    this.scheduleReconnect();
  }

  /** Gibt heraus, was diese Verbindung nicht nachweislich zugestellt hat. */
  private writeOff(): void {
    const offen = this.#ledger.takeUnconfirmed();
    if (offen.length > 0) {
      this.options.onUndelivered?.(offen);
    }
  }

  /**
   * Die erste Antwort des Servers: Ab hier gilt die Verbindung als offen, und
   * erst ab hier wird nachgesendet.
   */
  private bestaetige(): void {
    if (this.#bestaetigt) {
      return;
    }
    this.#bestaetigt = true;
    this.stopJoinTimer();
    this.#attempt = 0;
    this.options.onStatus('open');
    this.options.onReady?.();
  }

  private stopJoinTimer(): void {
    if (this.#joinTimer !== null) {
      clearTimeout(this.#joinTimer);
      this.#joinTimer = null;
    }
  }

  private stopHeartbeat(): void {
    if (this.#heartbeat !== null) {
      clearInterval(this.#heartbeat);
      this.#heartbeat = null;
    }
  }

  /** Sendet, wenn möglich. Liefert `false`, wenn die Nachricht nicht hinausging. */
  send(message: ClientMessage): boolean {
    if (!this.connected) {
      return false;
    }
    this.sendRaw(message);
    return true;
  }

  /**
   * Sendet und hält nach, ob es ankommt.
   *
   * `true` heißt hier nur: Es ging hinaus. Angekommen ist es erst, wenn ein
   * `pong` auf einen später gesendeten `ping` eintrifft. Stellt sich die
   * Verbindung vorher als tot heraus, kommt die Nachricht über `onUndelivered`
   * zurück.
   */
  sendTracked(message: ClientMessage): boolean {
    if (!this.connected) {
      return false;
    }
    this.sendRaw(message, true);
    return true;
  }

  private sendRaw(message: ClientMessage, tracked = false): void {
    const socket = this.#socket;
    if (socket === null) {
      return;
    }
    socket.send(encodeClientMessage(message));

    if (message.type === 'ping') {
      this.#ledger.pingSent(message.sentAt);
    } else if (tracked) {
      this.#ledger.sentTracked(message);
    } else {
      this.#ledger.sent();
    }
  }

  close(): void {
    this.#closedByUs = true;
    if (this.#onlineListener !== null) {
      window.removeEventListener('online', this.#onlineListener);
      this.#onlineListener = null;
    }
    this.stopHeartbeat();
    if (this.#reconnectTimer !== null) {
      clearTimeout(this.#reconnectTimer);
      this.#reconnectTimer = null;
    }
    /*
     * Selbst geschlossen wird, wenn die Fahrt für dieses Gerät endet: Die
     * Ausfahrt wird verlassen oder beendet, der Server meldet `removed` oder
     * `tour_finished`, oder die Fahransicht wird verlassen. In den ersten
     * Fällen nimmt der Server ohnehin nichts mehr an. Nichts zurückgeben, und
     * ein späteres `open` beginnt mit leerem Buch.
     *
     * Der Preis: Ist die Leitung genau in diesem Augenblick schon tot, ohne
     * dass es jemand weiß, fehlen die letzten Sekunden vor dem Schließen.
     */
    this.#ledger = new DeliveryLedger();
    this.#socket?.close();
  }
}
